import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../i18n';

interface NavigationProps {
  scrollToSection: (sectionId: string) => void;
}

const Navigation: React.FC<NavigationProps> = ({ scrollToSection }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const location = useLocation();
  const { t } = useTranslation();

  const handleNavClick = (sectionId: string) => {
    setActiveSection(sectionId);
    setIsMobileMenuOpen(false);
    
    // If we're on the home page, scroll to section
    if (location.pathname === '/') {
      scrollToSection(sectionId);
    } else {
      // If we're on another page, navigate to home with hash
      window.location.href = `/#${sectionId}`;
    }
  };

  React.useEffect(() => {
    const originalOverflow = document.body.style.overflow;

    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMobileMenuOpen]);

  React.useEffect(() => {
    if (!isMobileMenuOpen) {
      setIsMobileMenuVisible(false);
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      setIsMobileMenuVisible(true);
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [isMobileMenuOpen]);

  return (
    <nav 
      className="sticky top-0 z-50 border-b border-[#e7d9ca]/70 bg-[rgba(255,250,244,0.82)] backdrop-blur-xl"
    >
      <div className="container mx-auto px-4">
        <div className="flex py-6">

          <div className="hidden md:flex w-full justify-center items-center space-x-12">
            <button
              onClick={() => handleNavClick('about')}
              className={`text-sm font-medium uppercase tracking-[0.18em] transition-all duration-300 ${
                activeSection === 'about' 
                  ? 'text-stone-900' 
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              {t('navigation.about')}
            </button>
            {/* <button
              onClick={() => handleNavClick('collection')}
              className={`text-xl font-medium transition-all duration-300 ${
                activeSection === 'collection' 
                  ? 'text-white border-b-2 white' 
                  : 'text-white hover:text-gray-100 hover:border-b-2 hover:white'
              }`}
            >
              Collection
            </button>
            <button
              onClick={() => handleNavClick('rentals')}
              className={`text-xl font-medium transition-all duration-300 ${
                activeSection === 'rentals' 
                  ? 'text-white border-b-2 white' 
                  : 'text-white hover:text-gray-100 hover:border-b-2 hover:white'
              }`}
            >
              Rentals
            </button> */}

<Link
              to="/gallery"
              className="text-sm font-medium uppercase tracking-[0.18em] text-stone-600 transition-all duration-300 hover:text-stone-900"
            >
              {t('navigation.gallery')}
            </Link>
            <button
              onClick={() => handleNavClick('home')}
              className="text-3xl font-elegant tracking-[0.2em] text-stone-900"
            >
              <span className="text-[#b8946f]">DELYTH</span>

            </button>

            <button
              onClick={() => handleNavClick('reviews')}
              className={`text-sm font-medium uppercase tracking-[0.18em] transition-all duration-300 ${
                activeSection === 'reviews' 
                  ? 'text-stone-900' 
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              {t('navigation.reviews')}
            </button>
            <button
              onClick={() => handleNavClick('contact')}
              className={`text-sm font-medium uppercase tracking-[0.18em] transition-all duration-300 ${
                activeSection === 'contact' 
                  ? 'text-stone-900' 
                  : 'text-stone-600 hover:text-stone-900'
              }`}
            >
              {t('navigation.contact')}
            </button>
          </div>
          {/* Mobile menu button */}
          <div className="md:hidden flex justify-between items-center space-x-4 w-full">
            <Link to="/" className="text-3xl font-elegant tracking-[0.18em] text-stone-900 flex">
                <span className="text-[#b8946f]">DELYTH</span>
              </Link>
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-stone-800 hover:text-stone-950 transition-colors duration-300"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
          </div>
        </div>
        
        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div
            className={`md:hidden fixed inset-0 z-50 bg-[rgba(39,28,20,0.35)] backdrop-blur-sm transition-opacity duration-300 ${
              isMobileMenuVisible ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <div
              className="flex min-h-screen items-end px-0 pt-24"
              onClick={(event) => event.stopPropagation()}
            >
              <div
                className={`relative w-full rounded-t-[28px] border border-[#eadfd2] border-b-0 border-x-0 bg-[rgba(255,248,240,0.96)] px-4 pb-12 pt-4 shadow-2xl backdrop-blur-xl transition-all duration-300 ease-out ${
                  isMobileMenuVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
              >
                <div className="mb-4 flex items-center justify-between">
                  <div className="mx-auto h-1.5 w-12 rounded-full bg-stone-300" />
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="absolute right-6 top-8 flex h-10 w-10 items-center justify-center rounded-full border border-stone-200 bg-white/70 text-stone-700 transition-colors duration-300 hover:bg-white"
                    aria-label="Close menu"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="mb-4 pl-4 text-left">
                  <Link
                    to="/"
                    className="inline-flex text-3xl font-elegant tracking-[0.18em] text-stone-900"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="text-[#b8946f]">DELYTH</span>
                  </Link>
                </div>

                <div className="flex flex-col gap-2 text-center">
                <button
                  onClick={() => handleNavClick('home')}
                  className={`w-full rounded-2xl px-5 py-3 text-sm font-medium uppercase tracking-[0.16em] transition-all duration-300 ${
                    activeSection === 'home'
                      ? 'bg-[#f1e3d5] text-stone-900'
                      : 'text-stone-700 hover:bg-[#f7ede3] hover:text-stone-900'
                  }`}
                >
                  {t('navigation.home')}
                </button>
                <button
                  onClick={() => handleNavClick('about')}
                  className={`w-full rounded-2xl px-5 py-3 text-sm font-medium uppercase tracking-[0.16em] transition-all duration-300 ${
                    activeSection === 'about'
                      ? 'bg-[#f1e3d5] text-stone-900'
                      : 'text-stone-700 hover:bg-[#f7ede3] hover:text-stone-900'
                  }`}
                >
                  {t('navigation.about')}
                </button>
                <button
                  onClick={() => handleNavClick('collection')}
                  className={`w-full rounded-2xl px-5 py-3 text-sm font-medium uppercase tracking-[0.16em] transition-all duration-300 ${
                    activeSection === 'collection'
                      ? 'bg-[#f1e3d5] text-stone-900'
                      : 'text-stone-700 hover:bg-[#f7ede3] hover:text-stone-900'
                  }`}
                >
                  {t('navigation.collection')}
                </button>
                <button
                  onClick={() => handleNavClick('rentals')}
                  className={`w-full rounded-2xl px-5 py-3 text-sm font-medium uppercase tracking-[0.16em] transition-all duration-300 ${
                    activeSection === 'rentals'
                      ? 'bg-[#f1e3d5] text-stone-900'
                      : 'text-stone-700 hover:bg-[#f7ede3] hover:text-stone-900'
                  }`}
                >
                  {t('navigation.rentals')}
                </button>
                <Link
                  to="/gallery"
                  className="block w-full rounded-2xl px-5 py-3 text-sm font-medium uppercase tracking-[0.16em] text-stone-700 transition-all duration-300 hover:bg-[#f7ede3] hover:text-stone-900"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('navigation.gallery')}
                </Link>
                <button
                  onClick={() => handleNavClick('reviews')}
                  className={`w-full rounded-2xl px-5 py-3 text-sm font-medium uppercase tracking-[0.16em] transition-all duration-300 ${
                    activeSection === 'reviews'
                      ? 'bg-[#f1e3d5] text-stone-900'
                      : 'text-stone-700 hover:bg-[#f7ede3] hover:text-stone-900'
                  }`}
                >
                  {t('navigation.reviews')}
                </button>
                <button
                  onClick={() => handleNavClick('contact')}
                  className={`w-full rounded-2xl px-5 py-3 text-sm font-medium uppercase tracking-[0.16em] transition-all duration-300 ${
                    activeSection === 'contact'
                      ? 'bg-[#f1e3d5] text-stone-900'
                      : 'text-stone-700 hover:bg-[#f7ede3] hover:text-stone-900'
                  }`}
                >
                  {t('navigation.contact')}
                </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
